import cn from 'classnames'
import type { CSSProperties, ReactNode } from 'react'

import { getAttentionSeverityColor, isAttentionSeverityTop } from '../../configs/severity'
import type { TAttentionStatusSeverity } from '../../model/types'

import styles from './AttentionSeverityPlate.module.scss'

interface IAttentionSeverityPlateProps {
  severity: TAttentionStatusSeverity
  /** What the plate carries: a count, or the level's own word where there is nothing to count. */
  children: ReactNode
  /** Diameter of the plate. It stays a circle at any size — what it holds is what gives way. */
  size?: number
  /** Whether the worst level pulses here. A list where every row is that level would otherwise
   *  beat as one, so a view that repeats the plate turns it off on all but the one it leads. */
  pulse?: boolean
  className?: string
}

/** The plate rows are built around, and the size the tile's lists draw it at. */
const PLATE_SIZE = 28

/**
 * The mark every attention row carries: a circle in the level's colour holding what that row
 * counts, or the level's own word where it counts nothing. The worst level pulses, so the row to
 * act on first is found before a word of it is read.
 *
 * One plate for every list the tile draws — its rows, the board's column heads — so a level looks
 * the same wherever it is met.
 */
export const AttentionSeverityPlate = ({
  severity,
  children,
  size = PLATE_SIZE,
  pulse = true,
  className,
}: IAttentionSeverityPlateProps) => (
  <span
    className={cn(
      styles.plate,
      { [styles.platePulsing]: pulse && isAttentionSeverityTop(severity) },
      className
    )}
    style={
      {
        '--attention-plate-size': `${size}px`,
        '--attention-plate-color': getAttentionSeverityColor(severity),
      } as CSSProperties
    }
  >
    <span className={styles.plateContent}>{children}</span>
  </span>
)

AttentionSeverityPlate.displayName = 'AttentionSeverityPlate'
